import React, { useMemo, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, IconButton, InputAdornment, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import PendingActionsRounded from '@mui/icons-material/PendingActionsRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import ChatPopup from './CommentPopup';
import useDemandes, { normalize } from '../hooks/useDemandes';
import { getCommentaires } from '../../../services/CommentaireService';
import { commonGridSx, gridLocaleText } from '../../../utils/gridStyles';
import { DataGrid } from '../../../components/SortableDataGrid/SortableDataGrid';

dayjs.extend(relativeTime);

const STATUSES = [
    { value: 'en attente', label: 'En attente', Icon: PendingActionsRounded, color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
    { value: 'livrée', label: 'Livrée', Icon: CheckCircleRounded, color: '#166534', bg: '#f0fdf4', border: '#86efac' },
    { value: 'refusée', label: 'Refusée', Icon: CancelRounded, color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' },
];
const statusMeta = (s) => STATUSES.find((x) => normalize(x.value) === normalize(s)) || STATUSES[0];

const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
const fmt = (iso) => (iso ? dayjs(iso).format('DD/MM/YYYY') : '—');

// Status shown as a soft pill; clicking it opens the list of statuses.
function StatusPill({ value, onChange }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [saving, setSaving] = useState(false);
    const meta = statusMeta(value);

    const pick = async (next) => {
        setAnchorEl(null);
        if (normalize(next) === normalize(value)) return;
        setSaving(true);
        try { await onChange(next); } finally { setSaving(false); }
    };

    return (
        <>
            <Button
                size="small"
                onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
                disabled={saving}
                endIcon={!saving && <ArrowDropDownIcon sx={{ ml: -0.5 }} />}
                startIcon={saving ? <CircularProgress size={14} sx={{ color: meta.color }} /> : <meta.Icon sx={{ fontSize: '16px !important' }} />}
                sx={{
                    height: 28, px: 1.25, borderRadius: '999px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem',
                    color: `${meta.color} !important`, background: meta.bg, border: `1px solid ${meta.border}`,
                    '&:hover': { background: meta.bg, filter: 'brightness(0.97)' },
                }}
            >
                {meta.label}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} slotProps={{ paper: { sx: { borderRadius: '10px', minWidth: 180 } } }}>
                {STATUSES.map((s) => (
                    <MenuItem key={s.value} selected={normalize(s.value) === normalize(value)} onClick={() => pick(s.value)} sx={{ gap: 1, fontSize: '0.9rem' }}>
                        <s.Icon fontSize="small" sx={{ color: s.color }} /> {s.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

const StatCard = ({ label, count, active, onClick, color = '#03407b', bg = '#ebf4fb', Icon }) => (
    <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        sx={{
            flex: '1 1 150px', display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, borderRadius: '12px', cursor: 'pointer',
            background: active ? bg : '#fff', border: `1.5px solid ${active ? color : '#e2e8f0'}`,
            transition: 'all 0.15s ease', '&:hover': { borderColor: color },
            '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: 2 },
        }}
    >
        <Box sx={{ width: 34, height: 34, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, color }}>
            {Icon ? <Icon sx={{ fontSize: 19 }} /> : <InboxOutlined sx={{ fontSize: 19 }} />}
        </Box>
        <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{count}</Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{label}</Typography>
        </Box>
    </Box>
);

export default function DemandesBoard({ typeFilter, withPeriod = false, emptyLabel }) {
    const { loading, error, filtered, counts, search, setSearch, statusFilter, setStatusFilter, changeStatus } = useDemandes(typeFilter);
    const user = useMemo(() => JSON.parse(localStorage.getItem('userData') || 'null'), []);

    const [chat, setChat] = useState({ open: false, demandeId: null, name: '', messages: [], loadingId: null });

    const loadComments = async (demandeId) => {
        try {
            const { data } = await getCommentaires(demandeId);
            return { name: data.collaborateur || 'Collaborateur', messages: (data.commentaires || []).map((c) => ({ contenu: c.contenu, date: c.date, auteurType: c.auteurType })) };
        } catch {
            return { name: 'Collaborateur', messages: [] };
        }
    };

    const openChat = async (row) => {
        setChat((c) => ({ ...c, loadingId: row.id }));
        const { name, messages } = await loadComments(row.id);
        setChat({ open: true, demandeId: row.id, name, messages, loadingId: null });
    };

    const columns = [
        {
            field: 'displayApplicant', headerName: 'DEMANDEUR', flex: 1.4, minWidth: 200,
            renderCell: (p) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, height: '100%' }}>
                    <Avatar sx={{ width: 30, height: 30, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b' }}>
                        {p.value ? initials(p.value) : '…'}
                    </Avatar>
                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: p.value ? '#0f172a' : '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.value || 'Chargement…'}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'type', headerName: 'TYPE', flex: 1.2, minWidth: 180,
            renderCell: (p) => (
                <Typography title={p.value} sx={{ fontSize: '0.88rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.value || '—'}</Typography>
            ),
        },
        ...(withPeriod ? [{
            field: 'startDate', headerName: 'PÉRIODE', flex: 1.2, minWidth: 210,
            renderCell: (p) => {
                const { startDate, endDate } = p.row;
                const days = startDate && endDate ? dayjs(endDate).diff(dayjs(startDate), 'day') + 1 : null;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.88rem', color: '#0f172a' }}>{fmt(startDate)} → {fmt(endDate)}</Typography>
                        {days > 0 && (
                            <Box sx={{ px: 0.75, py: 0.1, borderRadius: '6px', background: '#ebf4fb', color: '#03407b', fontSize: '0.72rem', fontWeight: 700 }}>
                                {days} j
                            </Box>
                        )}
                    </Box>
                );
            },
        }] : []),
        {
            field: 'demandeDate', headerName: 'DEMANDÉE LE', width: 160,
            renderCell: (p) => (
                <Box sx={{ lineHeight: 1.2 }}>
                    <Typography sx={{ fontSize: '0.88rem', color: '#0f172a' }}>{fmt(p.value)}</Typography>
                    {p.value && <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{dayjs(p.value).locale('fr').fromNow()}</Typography>}
                </Box>
            ),
        },
        {
            field: 'status', headerName: 'STATUT', width: 170,
            renderCell: (p) => (
                <StatusPill value={p.value} onChange={(next) => changeStatus(p.row.id, next, user?.uuid)} />
            ),
        },
        {
            field: 'chat', headerName: 'DISCUSSION', width: 120, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
            renderCell: (p) => (
                <Tooltip title="Ouvrir la discussion" arrow>
                    <IconButton size="small" onClick={() => openChat(p.row)}
                        sx={{ color: '#03407b', background: '#ebf4fb', borderRadius: '8px', '&:hover': { background: '#dbeefa' } }}>
                        {chat.loadingId === p.row.id ? <CircularProgress size={16} /> : <ChatBubbleOutlineOutlined sx={{ fontSize: 18 }} />}
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <StatCard label="Toutes les demandes" count={counts.all} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
                {STATUSES.map((s) => (
                    <StatCard key={s.value} label={s.label} count={counts[s.value]} Icon={s.Icon} color={s.color} bg={s.bg}
                        active={statusFilter === s.value} onClick={() => setStatusFilter(statusFilter === s.value ? 'all' : s.value)} />
                ))}
            </Box>

            <TextField
                size="small"
                placeholder="Rechercher un demandeur, un type, une date…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ maxWidth: 440, '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff' } }} slotProps={{ input: {
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                    endAdornment: search && (
                        <InputAdornment position="end">
                            <Close onClick={() => setSearch('')} sx={{ fontSize: 18, color: '#94a3b8', cursor: 'pointer' }} />
                        </InputAdornment>
                    ),
                } }}
            />

            <Box sx={{ height: 'calc(100vh - 380px)', minHeight: 360, borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden' }}>
                {error ? (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#b91c1c' }}>
                        <Typography sx={{ fontWeight: 700 }}>{error}</Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>Veuillez réessayer plus tard.</Typography>
                    </Box>
                ) : (
                    <DataGrid
                        rows={filtered}
                        columns={columns}
                        getRowId={(r) => r.id}
                        loading={loading}
                        rowHeight={56}
                        disableRowSelectionOnClick
                        initialState={{
                            sorting: { sortModel: [{ field: 'demandeDate', sort: 'desc' }] },
                            pagination: { paginationModel: { pageSize: 50 } },
                        }}
                        pageSizeOptions={[25, 50, 100]}
                        slots={{
                            noRowsOverlay: () => (
                                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#94a3b8' }}>
                                    <InboxOutlined sx={{ fontSize: 36 }} />
                                    <Typography sx={{ fontSize: '0.9rem', color: '#475569', fontWeight: 600 }}>{emptyLabel}</Typography>
                                    {(search || statusFilter !== 'all') && (
                                        <Typography sx={{ fontSize: '0.8rem' }}>Essayez d'enlever un filtre ou de modifier la recherche.</Typography>
                                    )}
                                </Box>
                            ),
                        }}
                        slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
                        localeText={gridLocaleText}
                        sx={{ ...commonGridSx, border: 'none' }}
                    />
                )}
            </Box>

            <ChatPopup
                open={chat.open}
                handleClose={() => setChat((c) => ({ ...c, open: false }))}
                title={`Discussion - ${chat.name}`}
                messages={chat.messages}
                demandeId={chat.demandeId}
                traiteePar={user?.uuid}
                onSend={async () => {
                    const { messages } = await loadComments(chat.demandeId);
                    setChat((c) => ({ ...c, messages }));
                }}
            />
        </Box>
    );
}

